/**
 * What a CI box card says, worked out without React. org-admin#97.
 *
 * The box's health comes from its own agent (RidgeWill/docs#67) through
 * fleet-api (fleet-api#22). fleet-api has already decided the two things that
 * are easy to get wrong, `state` (silence is a state) and each runner's
 * `listener`; this file only puts them into words.
 */
import type { Box, BoxAction, BoxRunner, BoxVerb, RunningJob } from './fleet-api.js';
import type { Tone } from './fleet-view.js';

/** Past this share of the root disk, a box needs its Docker cache pruned. */
export const DISK_WARN_SHARE = 0.8;
/** The i5-9500 throttles near 100 °C; 90 is the point to go and look. */
export const CPU_HOT_C = 90;

/**
 * Does this box run Docker, as far as its last report says?
 *
 * The Linux agent always sends `docker_gb`; the Mac mini's agent sends null,
 * because it has no Docker. So a box whose report carries no Docker figure
 * gets no Docker prune, no Docker metric and no "run the prune" advice.
 * A Linux box whose Docker is down reports null too, and loses the button
 * while it is: a prune could not run then anyway.
 */
export function hasDocker(report: { docker_gb: number | null } | null | undefined): boolean {
  return report != null && report.docker_gb !== null;
}

/** `3 d 2 h`, `5 h 12 min`, `4 min`, `40 s`. */
export function durationText(s: number | null | undefined): string {
  if (s === null || s === undefined || !Number.isFinite(s)) return '—';
  const whole = Math.max(0, Math.floor(s));
  if (whole < 60) return `${whole} s`;
  if (whole < 3600) return `${Math.floor(whole / 60)} min`;
  if (whole < 86_400) {
    const m = Math.floor((whole % 3600) / 60);
    return `${Math.floor(whole / 3600)} h${m ? ` ${m} min` : ''}`;
  }
  const h = Math.floor((whole % 86_400) / 3600);
  return `${Math.floor(whole / 86_400)} d${h ? ` ${h} h` : ''}`;
}

export function boxStateView(box: Box): { tone: Tone; label: string } {
  switch (box.state) {
    case 'reporting':
      return { tone: 'good', label: 'Reporting' };
    case 'silent':
      return { tone: 'danger', label: `Silent for ${durationText(box.since_report_s)}` };
    case 'never_reported':
      return { tone: 'muted', label: 'Never reported' };
    case 'revoked':
      return { tone: 'muted', label: 'Token revoked' };
  }
}

/** `headless-1-a` from `ridgewill-runner@headless-1-a.service`. */
export function runnerName(unit: string): string {
  return unit.replace(/^ridgewill-runner@/, '').replace(/\.service$/, '');
}

/**
 * A runner as its box sees it, beside what GitHub says.
 *
 * Both sources are shown rather than one picked (the 12 Sep comment on #97):
 * "Connected, GitHub reports offline" says the runner is fine and GitHub's view
 * of it is not, which is what somebody needs before restarting six services.
 */
export function listenerView(runner: BoxRunner, githubStatus?: string): { tone: Tone; label: string } {
  switch (runner.listener) {
    case 'connected':
      return githubStatus === 'offline'
        ? { tone: 'good', label: 'Connected, GitHub reports offline' }
        : { tone: 'good', label: 'Connected to GitHub' };
    case 'no_connection':
      // Also true for the seconds a runner spends registering between jobs, so
      // warn here, and alert only when it lasts (fleet-alerts.ts).
      return githubStatus === 'offline'
        ? { tone: 'warn', label: 'No connection, and GitHub reports offline' }
        : { tone: 'warn', label: 'No connection to GitHub' };
    case 'not_running':
      return { tone: 'danger', label: `Service ${runner.active_state ?? 'not running'}` };
    case 'unknown':
      return { tone: 'muted', label: 'Connection not reported' };
  }
}

/** Used over total, or null when either is missing. */
export function share(used: number | null, total: number | null): number | null {
  return used === null || total === null || total <= 0 ? null : Math.min(1, Math.max(0, used / total));
}

export function diskTone(fraction: number | null): Tone {
  return fraction === null ? 'muted' : fraction > DISK_WARN_SHARE ? 'warn' : 'good';
}

// ── BOX ACTIONS (org-admin#99) ───────────────────────────────────────
//
// An admin queues one of four verbs; the box claims it with its next report and
// posts the result (fleet-api#24). So nothing happens at the click: the
// confirmation and the list both have to say "when the box next reports", and
// a box that is not reporting will never pick it up.

/** fleet-api expires an action no box has claimed in this long (QUEUE_EXPIRY_S). */
export const ACTION_EXPIRY_MIN = 10;

/** A verb as a button and a list entry say it. */
export function verbLabel(verb: BoxVerb, target: string | null): string {
  switch (verb) {
    case 'report':
      return 'Send a report';
    case 'restart_runner':
      return target ? `Restart ${target}` : 'Restart a runner';
    case 'prune_docker':
      return 'Prune Docker';
    case 'reboot':
      return 'Reboot';
  }
}

/** The action a box has queued or running. fleet-api holds one at a time per box. */
export function inFlight(box: Box): BoxAction | null {
  return box.actions?.find((a) => a.status === 'queued' || a.status === 'claimed') ?? null;
}

/** Why the action buttons are off for this box, or null when they are on. */
export function actionBlocked(box: Box): string | null {
  if (box.state === 'revoked') return `${box.name}'s token is revoked, so it cannot pick up an action.`;
  const active = inFlight(box);
  if (active) {
    return `${box.name} already has ${verbLabel(active.verb, active.target)} ${active.status === 'queued' ? 'waiting' : 'running'}. A box takes one action at a time.`;
  }
  return null;
}

export function actionStatusView(action: BoxAction): { tone: Tone; label: string } {
  switch (action.status) {
    case 'queued':
      return { tone: 'muted', label: 'Waiting for the box' };
    case 'claimed':
      return { tone: 'muted', label: 'Running' };
    case 'succeeded':
      return { tone: 'good', label: 'Succeeded' };
    case 'failed':
      return { tone: 'danger', label: 'Failed' };
    case 'expired':
      return { tone: 'warn', label: 'Expired' };
    case 'cancelled':
      return { tone: 'muted', label: 'Cancelled' };
  }
}

/** The last line an action printed, for a one-line list entry. The whole output is its title. */
export function lastLine(output: string | null): string | null {
  const lines = (output ?? '').split('\n').map((l) => l.trim()).filter(Boolean);
  return lines.at(-1) ?? null;
}

const jobText = (j: RunningJob) => `${j.repo} ${j.job_name}`;
const listOf = (items: string[]) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items.at(-1)}`);

/**
 * What the confirmation says before a box action, so nobody learns what a
 * restart does to a running job by doing one.
 *
 * `running` is the collector's running jobs, or null when they could not be
 * read. With them, a restart or reboot names the jobs it will lose; without,
 * it still says a running job is lost, rather than implying there is none.
 */
export function actionCopy(
  box: Box,
  verb: BoxVerb,
  target: string | null,
  running: RunningJob[] | null,
): { title: string; points: string[]; button: string; done: string } {
  const runners = (box.last_report?.runners ?? []).map((r) => runnerName(r.unit));
  const lostOn = (names: string[]) => (running ?? []).filter((j) => names.includes(j.runner_name));
  const lostPoint = (jobs: RunningJob[], on: string) =>
    jobs.length > 0
      ? `${on} is running ${listOf(jobs.map(jobText))} now. ${jobs.length === 1 ? 'That job is' : 'Those jobs are'} lost and ${jobs.length === 1 ? 'has' : 'have'} to be re-run.`
      : running === null
        ? `Could not read which jobs are running. Any job running on ${on} when this happens is lost and has to be re-run.`
        : `Nothing is running on ${on} right now. A job that starts before the box picks this up is lost and has to be re-run.`;

  let copy: { title: string; points: string[]; button: string; done: string };
  switch (verb) {
    case 'report':
      copy = {
        title: `Ask ${box.name} for a report?`,
        points: ['The box sends a fresh report and changes nothing else. It is a safe way to check that actions reach it.'],
        button: 'Send a report',
        done: `Asked ${box.name} for a report.`,
      };
      break;
    case 'restart_runner': {
      const runner = target ?? 'the runner';
      copy = {
        title: `Restart ${runner}?`,
        points: [`Restarts the ${runner} runner service on ${box.name}.`, lostPoint(lostOn([runner]), runner)],
        button: `Restart ${runner}`,
        done: `Queued a restart of ${runner}.`,
      };
      break;
    }
    case 'prune_docker':
      copy = {
        title: `Prune Docker on ${box.name}?`,
        points: [
          `Removes Docker images, stopped containers and build cache not used in the last 7 days, the same as the weekly prune${box.last_report?.docker_gb != null ? `. Docker holds ${box.last_report.docker_gb.toFixed(1)} GB now` : ''}.`,
          'Running jobs keep their containers. A later job that needs a removed image downloads it again, so it starts slower once.',
        ],
        button: 'Prune Docker',
        done: `Queued a Docker prune on ${box.name}.`,
      };
      break;
    case 'reboot':
      copy = {
        title: `Reboot ${box.name}?`,
        points: [
          `${box.name} goes offline while it restarts${runners.length > 0 ? `, and its runners ${listOf(runners)} with it` : ''}.`,
          lostPoint(lostOn(runners), box.name),
          'The box reports success just before it reboots, so Succeeded means the reboot started. The box is back when it reports again.',
        ],
        button: `Reboot ${box.name}`,
        done: `Queued a reboot of ${box.name}.`,
      };
      break;
  }

  copy.points.push(
    box.state === 'reporting'
      ? `${box.name} picks this up on its next report, within a minute.`
      : `${box.name} is not reporting, so this only happens if it reports again within ${ACTION_EXPIRY_MIN} minutes. After that it expires.`,
  );
  return copy;
}
